/** The notification centre.
 *
 * There is no notification model in the backend — no in-app notification table,
 * no read/unread state, no delivery log. Rather than show an empty shell or
 * invent a feed, this derives the things an agent would actually be notified
 * about from data the queue API already returns:
 *
 *   · SLA breaches          GET /tickets/?breaching=true
 *   · Awaiting triage       GET /tickets/?unassigned=true
 *   · Assigned to you       GET /tickets/?assigned_to_me=true
 *
 * Every row is a real ticket you can click. What is missing is stated in the
 * footer rather than papered over: these are live-derived, so they cannot be
 * marked read or dismissed, and nothing here covers mentions or approvals.
 */
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Bell, Inbox, UserCheck } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  ScrollArea,
  Skeleton,
} from '../../components/ui/primitives'
import { cn } from '../../lib/utils'
import { type AgentTicket, agentApi } from '../api'

interface Feed {
  key: string
  label: string
  icon: LucideIcon
  tone: string
  query: string
  /** Counts toward the badge — only things that need action now. */
  urgent: boolean
}

const FEEDS: Feed[] = [
  {
    key: 'breaching',
    label: 'SLA breached or breaching',
    icon: AlertTriangle,
    tone: 'text-critical',
    query: 'breaching=true',
    urgent: true,
  },
  {
    key: 'unassigned',
    label: 'Awaiting triage',
    icon: Inbox,
    tone: 'text-warning',
    query: 'unassigned=true',
    urgent: true,
  },
  {
    key: 'mine',
    label: 'Assigned to you',
    icon: UserCheck,
    tone: 'text-info',
    query: 'assigned_to_me=true',
    urgent: false,
  },
]

const [BREACHING, UNASSIGNED, MINE] = FEEDS

function useFeed(feed: Feed) {
  return useQuery({
    queryKey: ['notifications', feed.key],
    queryFn: () => agentApi.get<AgentTicket[]>(`/tickets/?${feed.query}`),
    // Polling, because there is no push channel. Deliberately slow: this is a
    // background signal, not a live console.
    refetchInterval: 120_000,
    staleTime: 60_000,
  })
}

/** `triggerClassName` exists for the administration bar, whose chrome is navy —
 *  the ghost button's hover resolves to the light accent and disappears there. */
export function NotificationCenter({ triggerClassName }: { triggerClassName?: string } = {}) {
  // Called one by one rather than mapped over FEEDS: a hook inside a loop is
  // only safe while the array never changes length, and that is not a property
  // worth relying on.
  const breaching = useFeed(BREACHING)
  const unassigned = useFeed(UNASSIGNED)
  const mine = useFeed(MINE)

  const results = [
    { feed: BREACHING, query: breaching },
    { feed: UNASSIGNED, query: unassigned },
    { feed: MINE, query: mine },
  ]

  const urgentCount = results
    .filter(({ feed }) => feed.urgent)
    .reduce((total, { query }) => total + (query.data?.length ?? 0), 0)

  const loading = results.some(({ query }) => query.isLoading)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            urgentCount > 0 ? `Notifications, ${urgentCount} needing attention` : 'Notifications'
          }
          className={cn('relative', triggerClassName)}
        >
          <Bell />
          {urgentCount > 0 && (
            <span className="absolute right-1 top-1 grid min-w-[1rem] place-items-center rounded-full bg-critical px-1 text-[0.625rem] font-semibold leading-4 text-critical-foreground">
              {urgentCount > 99 ? '99+' : urgentCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[22rem] p-0">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <p className="text-sm font-semibold">Notifications</p>
          {urgentCount > 0 && (
            <Badge variant="critical" size="sm">
              {urgentCount} need attention
            </Badge>
          )}
        </div>

        <ScrollArea className="max-h-[26rem]">
          {loading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 4 }, (_, row) => (
                <Skeleton key={row} className="h-8 w-full" />
              ))}
            </div>
          ) : (
            <div className="py-1">
              {results.map(({ feed, query }) => (
                <FeedSection key={feed.key} feed={feed} tickets={query.data ?? []} />
              ))}
            </div>
          )}
        </ScrollArea>

        <div className="border-t border-border px-3 py-2">
          <p className="text-xs text-muted-foreground">
            Derived live from the ticket queue. There is no notification model in the backend, so
            these cannot be marked read, and mentions and approvals are not covered.
          </p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function FeedSection({ feed, tickets }: { feed: Feed; tickets: AgentTicket[] }) {
  if (tickets.length === 0) return null

  return (
    <section className="px-1 py-1">
      <p className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-muted-foreground">
        <feed.icon className={cn('size-3.5', feed.tone)} aria-hidden />
        {feed.label}
        <span className="numeric ml-auto">{tickets.length}</span>
      </p>
      <ul>
        {tickets.slice(0, 5).map((ticket) => (
          <li key={ticket.ref}>
            <Link
              to={`/agent/tickets/${ticket.ref}`}
              className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent"
            >
              <span className="font-mono text-xs text-muted-foreground">{ticket.ref}</span>
              <span className="truncate">{ticket.title}</span>
            </Link>
          </li>
        ))}
      </ul>
      {tickets.length > 5 && (
        <Link
          to={`/agent/tickets?${feed.query}`}
          className="block px-2 py-1 text-xs text-primary hover:underline"
        >
          View all {tickets.length}
        </Link>
      )}
    </section>
  )
}
